/**
 * Coerce a value to a finite number; non-finite/null/undefined → 0.
 */
export function safeNum(value: number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (!Number.isFinite(value)) return 0;
  return value;
}

/**
 * Format a dollar amount (decimal, NOT cents) with thousand separators.
 */
export function formatDollars(value: number, decimals = 2): string {
  const n = safeNum(value);
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return n < 0 ? `-$${formatted}` : `$${formatted}`;
}

/**
 * Format integer cents (per ADR-0006 wire shape) as a dollar string.
 * Accepts bigint or numeric string (the canonical wire shape).
 */
export function formatCents(cents: bigint | string): string {
  const asBigint = typeof cents === 'string' ? BigInt(cents) : cents;
  const negative = asBigint < 0n;
  const abs = negative ? -asBigint : asBigint;
  const dollars = abs / 100n;
  const remainder = abs % 100n;
  const dollarStr = dollars.toLocaleString('en-US');
  const centStr = remainder.toString().padStart(2, '0');
  const formatted = `${dollarStr}.${centStr}`;
  return negative ? `-$${formatted}` : `$${formatted}`;
}

/**
 * Format a decimal value (0.0825 → "8.25%") with optional decimal places.
 */
export function formatPct(value: number, decimals = 2): string {
  const n = safeNum(value);
  const formatted = (n * 100).toFixed(decimals);
  return `${formatted}%`;
}
